/*
 * Listicle landing pages (listicle-* sections).
 *
 * - Sticky bar: shows once the hero ([data-ls-hero]) has scrolled past and
 *   hides while the buy module ([data-lpb]) is on screen. Without either
 *   target it shows after 600px of scroll.
 * - Video tiles: play muted while in view, pause out of view. The tile
 *   button toggles sound; an optional [data-ls-pause] button pauses.
 *   [data-ls-manual] tiles don't autoplay: they start once a button is used.
 * - Video dialog: proof links with [data-ls-video-dialog] open a <dialog>
 *   and only then inject the YouTube iframe; closing removes it.
 * - Analytics: jump, proof, dialog and sticky clicks go to window.dataLayer.
 *
 * Every section that needs it includes this file, so it guards against
 * running twice. Theme editor section loads re-run setup.
 */
(() => {
  if (window.lsListicle) return;
  window.lsListicle = { version: 1 };

  const FALLBACK_SCROLL = 600;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let stickyForced = false;
  const YOUTUBE_ID = /(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([\w-]{11})/;

  function track(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  }

  /* Sticky bar ----------------------------------------------------------- */

  function showSticky(bar, visible) {
    if (bar.hidden === !visible) return;
    bar.hidden = !visible;
    bar.inert = !visible;
    const root = document.documentElement;
    root.classList.toggle('ls-sticky-on', visible);
    root.style.setProperty('--ls-sticky-height', visible ? `${bar.offsetHeight}px` : '0px');
  }

  function setupSticky() {
    const bar = document.querySelector('[data-ls-sticky]');
    if (!bar) return () => {};
    const hero = document.querySelector('[data-ls-hero]');
    const buy = document.querySelector('[data-lpb]');
    const state = { heroPassed: false, buyVisible: false };

    const resizeObserver = new ResizeObserver(() => {
      if (!bar.hidden) {
        document.documentElement.style.setProperty('--ls-sticky-height', `${bar.offsetHeight}px`);
      }
    });
    resizeObserver.observe(bar);

    if (hero && buy) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === hero) {
            state.heroPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          } else {
            state.buyVisible = entry.isIntersecting;
          }
        }
        showSticky(bar, stickyForced || (state.heroPassed && !state.buyVisible));
      });
      observer.observe(hero);
      observer.observe(buy);
      return () => {
        observer.disconnect();
        resizeObserver.disconnect();
        showSticky(bar, false);
      };
    }

    const onScroll = () => showSticky(bar, stickyForced || window.scrollY > FALLBACK_SCROLL);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      showSticky(bar, false);
    };
  }

  /* Video tiles ---------------------------------------------------------- */

  function setupVideos() {
    const tiles = document.querySelectorAll('[data-ls-video]');
    if (!tiles.length) return () => {};
    // [data-ls-manual] tiles wait for the visitor: they start out "paused by
    // the user", so the observer leaves them alone until a button plays them.
    tiles.forEach((tile) => {
      const video = tile.querySelector('video');
      if (video && tile.hasAttribute('data-ls-manual') && video.lsUserPaused === undefined) video.lsUserPaused = true;
    });
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) continue;
          const visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          if (!visible) video.pause();
          else if (!video.lsUserPaused && (!reducedMotion.matches || !video.muted)) video.play().catch(() => {});
        }
      },
      { threshold: 0.25 }
    );
    tiles.forEach((tile) => observer.observe(tile));
    return () => observer.disconnect();
  }

  function toggleSound(button) {
    const video = button.closest('[data-ls-video]')?.querySelector('video');
    if (!video) return;
    video.muted = !video.muted;
    if (!video.muted) {
      document.querySelectorAll('[data-ls-video] video').forEach((other) => {
        if (other === video) return;
        other.muted = true;
        other.closest('[data-ls-video]')?.querySelector('[data-ls-sound]')?.setAttribute('aria-pressed', 'false');
      });
    }
    if (video.paused) {
      video.lsUserPaused = false;
      video.play().catch(() => {});
    }
    button.setAttribute('aria-pressed', String(!video.muted));
  }

  // Optional pause button ([data-ls-pause], aria-pressed = paused) for tiles
  // that loop longer than a few seconds. A paused tile stays paused when it
  // scrolls back into view.
  function togglePause(button) {
    const video = button.closest('[data-ls-video]')?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      video.lsUserPaused = false;
      video.play().catch(() => {});
    } else {
      video.lsUserPaused = true;
      video.pause();
    }
  }

  // Media events don't bubble, so listen in the capture phase.
  ['play', 'pause'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        const video = event.target;
        if (!(video instanceof HTMLVideoElement)) return;
        video.closest('[data-ls-video]')?.querySelector('[data-ls-pause]')?.setAttribute('aria-pressed', String(video.paused));
      },
      true
    )
  );

  /* Video dialog --------------------------------------------------------- */

  function openDialog(trigger) {
    const dialog = document.getElementById(trigger.dataset.lsVideoDialog);
    const match = trigger.href.match(YOUTUBE_ID);
    const frame = dialog?.querySelector('[data-ls-dialog-frame]');
    if (!dialog || !frame || !match || typeof dialog.showModal !== 'function') return false;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${match[1]}?autoplay=1&rel=0&playsinline=1`;
    iframe.title = dialog.getAttribute('aria-label') || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    frame.replaceChildren(iframe);

    dialog.lsTrigger = trigger;
    dialog.showModal();
    track('ls_video_open', { ls_check: trigger.dataset.lsProof || '' });
    return true;
  }

  // "close" doesn't bubble, so listen in the capture phase.
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-ls-dialog]')) return;
      dialog.querySelector('[data-ls-dialog-frame]').replaceChildren();
      dialog.lsTrigger?.focus();
    },
    true
  );

  /* Clicks --------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // A click on the backdrop lands on the dialog itself.
    if (target.matches('dialog[data-ls-dialog]')) {
      target.close();
      return;
    }

    const sound = target.closest('[data-ls-sound]');
    if (sound) {
      toggleSound(sound);
      return;
    }

    const pause = target.closest('[data-ls-pause]');
    if (pause) {
      togglePause(pause);
      return;
    }

    const link = target.closest('a');
    if (!link) return;
    if (link.dataset.lsJump) track('ls_jump', { ls_target: link.dataset.lsJump });
    if (link.hasAttribute('data-ls-sticky-cta')) track('ls_sticky_click');
    if (link.dataset.lsProof) {
      track('ls_proof', { ls_check: link.dataset.lsProof });
      const plain = event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
      if (plain && link.dataset.lsVideoDialog && openDialog(link)) event.preventDefault();
    }
  });

  /* Lifecycle ------------------------------------------------------------ */

  let cleanups = [];
  function setup() {
    cleanups.forEach((cleanup) => cleanup());
    cleanups = [setupSticky(), setupVideos()];
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup);
  else setup();

  document.addEventListener('shopify:section:load', setup);
  // Unload fires before the section leaves the DOM.
  document.addEventListener('shopify:section:unload', () => setTimeout(setup));

  // Theme editor: show the bar while its section is selected.
  document.addEventListener('shopify:section:select', (event) => {
    const bar = event.target.querySelector('[data-ls-sticky]');
    if (bar) {
      stickyForced = true;
      showSticky(bar, true);
    }
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('[data-ls-sticky]')) {
      stickyForced = false;
      setup();
    }
  });
})();
